import React, { useMemo, useCallback, useState, useRef } from 'react'
import styled from 'styled-components'

import { toggleRegionDropdown } from 'actions/UiActions'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import setRegionCookie from 'cookies/setRegionCookie'
import TextButton from 'components/Luxkit/Button/TextButton'
import { rem } from 'polished'
import Dropdown from 'components/Luxkit/Dropdown'
import { getHeight } from 'lib/getViewport'
import ListItem from 'components/Luxkit/List/ListItem'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import Flag from 'components/Luxkit/Flag'
import config from 'constants/config'
import { sortBy } from 'lib/arrayUtils'
import { useTranslation } from 'react-i18next'

const MenuItem = styled.div`
  position: relative;
`

const RegionDropdownContainer = styled(DropdownContainer)`
  overflow: hidden;
  max-height: ${rem(460)};

  &.fit-auto {
    padding: 0;
  }
`

const Scroller = styled.div`
  overflow-y: auto;
  height: 100%;
  padding: ${rem(8)};
`

const REGION_START_ORDER = ['AU', 'US', 'GB', 'NZ', 'SG']

interface Props {
  location: any;
  currentRegionCode: string;
  currentRegionName: string;
  className?: string;
}

function NavigationHeaderRegionMenu(props: Props) {
  const {
    currentRegionCode,
    location,
    className,
  } = props

  const [isOpen, setOpen] = useState(false)
  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
  const triggerRef = useRef()
  const { t } = useTranslation('common')
  const dropdownContentRef = useRef<HTMLDivElement>()
  const dispatch = useAppDispatch()
  const onMenuToggle = useCallback(() => {
    if (!isOpen) {
      // Set max-height of dropdown so it won't protrude outside the viewport.
      // We must do this dynamically, because the position of the toggle can vary
      // depending on presence of notifications above the menu bar.
      const contentRect = dropdownContentRef.current.getBoundingClientRect()
      const protrusion = contentRect.bottom - getHeight()
      setContentMaxHeight(contentRect.height - protrusion - 16)
    }

    setOpen(!isOpen)
  }, [isOpen])

  const onMenuClose = useCallback(() => {
    setOpen(false)
  }, [])

  const onRegionClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    const regionCode = e.currentTarget.dataset.regioncode
    dispatch(toggleRegionDropdown())
    if (regionCode !== currentRegionCode) {
      setRegionCookie(regionCode)
      window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + regionCode.toLowerCase()) + location.search
    }
  }

  // We want our biggest markets at the top of the list, in a specific order
  const sortedRegions = useMemo(() => {
    return sortBy(config.regions, (region) => {
      const regionOrder = REGION_START_ORDER.indexOf(region.code)
      if (regionOrder === -1) {
        // Put it at the bottom of the list if it's not in the desired order
        return 999
      }
      return regionOrder
    }, 'asc')
  }, [])

  const regionData = useMemo(() => config.regions.find(region => (region.code === currentRegionCode)), [currentRegionCode])

  if (config.regions.length < 2) {
    return null
  }

  return (<MenuItem
    className={className}
    role="presentation"
    aria-label={t('Select your region')}
  >
    <TextButton
      kind="tertiary"
      variant="dark"
      aria-expanded={isOpen}
      aria-haspopup
      onClick={onMenuToggle}
      ref={triggerRef}
      startIcon={regionData ? <Flag countryCode={currentRegionCode} /> : undefined}
      endIcon={regionData ? <DropdownChevron open={isOpen} /> : undefined}
    >
      {regionData && regionData.currencyCode}
      {!regionData && currentRegionCode}
    </TextButton>
    <Dropdown
      id="region-menu-dropdown"
      open={isOpen}
      onClose={onMenuClose}
      caretType="none"
      triggerRef={triggerRef}
    >
      <RegionDropdownContainer
        style={{ height: `${contentMaxHeight}px` }}
        fit="dropdown-items"
        ref={dropdownContentRef}
      >
        <Scroller>
          {sortedRegions.map(region => (
            <ListItem
              size="small"
              key={region.code}
              aria-label={t('SelectRegion', { regionName: t(region.name) })}
              data-regioncode={region.code}
              onClick={onRegionClick}
              startIcon={<Flag countryCode={region.code} />}
              title={t(region.name)}
            />
          ))}
        </Scroller>
      </RegionDropdownContainer>
    </Dropdown>

  </MenuItem>
  )
}

function mapStateToProps(state: App.State) {
  return {
    location: state.router.location,
    currentRegionCode: state.geo.currentRegionCode,
    currentRegionName: state.geo.currentRegionName,
  }
}

export default connect(mapStateToProps)(NavigationHeaderRegionMenu)
